import { useAuth } from '@/app/AuthContext.jsx';
import { CAPABILITY_FULL_LABELS } from '@/app/constants.js';
import { useAccent, useDensity, useExpFeatures } from '@/hooks';
import {
  accountTypeLabel,
  bankName,
  createPreference,
  deriveFullUserName,
  deriveUserInitials,
  formatDisplay as formatPhone,
  resolveThemeMode,
  toggleThemeMode,
} from '@/lib';
import { Button, Input, PageHead, Pager } from '@/ui';
import { useEffect, useState } from 'react';
import './Configuracion.css';
import { MiembrosTab as OrgMiembrosTab } from './Organizacion';

// Local boolean preferences backed by the shared createPreference primitive
// (same '1'/'0' localStorage encoding the panel used before). These have no
// applyAttr and no cross-tree consumer, so the screens hold their own React
// state and call <pref>.set on change.
const avisosPref = createPreference('rec:notif:avisos', {
  fallback: true,
  encode: (b) => (b ? '1' : '0'),
  decode: (s) => s === '1',
});
const atajosPref = createPreference('rec:notif:atajos', {
  fallback: true,
  encode: (b) => (b ? '1' : '0'),
  decode: (s) => s === '1',
});

// Stacked read-only field (label above value) shown as a DS input box. Editing
// happens through the section's modal, so the input is read-only here.
function Fld({ label, value, full }) {
  return (
    <label className={`form-field${full ? ' fld--full' : ''}`}>
      <span className="form-field__label">{label}</span>
      {/* '' fallback: a missing field must not flip the input to uncontrolled
          (cuenta defaults to {} without the story fixture). */}
      <Input value={value ?? ''} readOnly variant="mono" />
    </label>
  );
}

function PerfilTab({ cuenta, onEditar }) {
  const { user, activeCapability } = useAuth() ?? {};
  const initials = deriveUserInitials(user);
  const fullName = deriveFullUserName(user);
  // Role label derived from capability; no join date available in useAuth.
  const roleLabel = CAPABILITY_FULL_LABELS[activeCapability] ?? '';

  return (
    <div className="settings">
      <section className="set-section">
        <div className="set-section__head">
          <h3 className="set-section__title">Información personal</h3>
          <div className="set-section__right">
            <span className="note">Cómo te identificamos en el sistema.</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onEditar('personal')}
            >
              Editar
            </Button>
          </div>
        </div>
        <div className="set-section__body">
          {/* Profile head — real data from useAuth; join date omitted (not exposed by API) */}
          <div className="profile-head">
            <span className="profile-head__av" aria-hidden="true">
              {initials}
            </span>
            <div>
              <div className="tbl__primary">{fullName}</div>
              {roleLabel && <div className="note">{roleLabel}</div>}
            </div>
          </div>
          <div className="form-grid">
            <Fld label="Nombre" value={cuenta.nombre} />
            <Fld label="Apellido" value={cuenta.apellido} />
            <Fld label="Correo" value={cuenta.correo} full />
          </div>
        </div>
      </section>
    </div>
  );
}

// Org profile read view. Same DS input fields as the personal tab; editing
// happens through the modal.
function PerfilOrgTab({ perfil, perfilFailed, tipo, onEditar, canManageTeam }) {
  const p = perfil ?? {};
  return (
    <div className="settings">
      <section className="set-section">
        <div className="set-section__head">
          <h3 className="set-section__title">Datos del estudio</h3>
          <div className="set-section__right">
            <span className="note">
              {perfilFailed
                ? 'No se pudieron cargar.'
                : 'Visibles en comprobantes.'}
            </span>
            {/* Editar lo que no se pudo leer guarda blancos sobre datos
                reales, así que la acción no se ofrece hasta que la lectura
                funcione. */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => canManageTeam && onEditar('perfil')}
              disabled={!canManageTeam || perfilFailed}
              title={
                perfilFailed
                  ? 'No se pudo cargar el perfil'
                  : !canManageTeam
                    ? 'Solo administradores'
                    : undefined
              }
            >
              Editar
            </Button>
          </div>
        </div>
        <div className="set-section__body">
          <div className="form-grid">
            <Fld label="Razón social" value={p.legalName || '—'} full />
            <Fld label="Nombre de fantasía" value={p.tradeName || '—'} />
            <Fld label="Tipo" value={tipo || '—'} />
            <Fld label="Giro" value={p.businessActivity || '—'} full />
            <Fld label="Email" value={p.contactEmail || '—'} />
            <Fld label="Teléfono" value={formatPhone(p.contactPhone) || '—'} />
            <Fld label="Sitio web" value={p.website || '—'} full />
          </div>
        </div>
      </section>
    </div>
  );
}

function SeguridadTab({ cuenta, onEditar }) {
  // Two-step verification has no endpoint and no login step yet. Internal
  // users see it as a non-interactive «Próx» preview, behind the same gate as
  // "Funciones experimentales" and in the same shape as "Resumen por correo".
  const { isInternal } = useAuth() ?? {};

  return (
    <div className="settings">
      <section className="set-section">
        <div className="set-section__head">
          <h3 className="set-section__title">Contraseña</h3>
          <div className="set-section__right">
            <span className="note">Cambia tu contraseña de acceso.</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onEditar('password')}
            >
              Cambiar contraseña
            </Button>
          </div>
        </div>
        <div className="set-section__body">
          <div className="form-grid">
            <Fld label="Contraseña" value={cuenta.passwordMask} full />
          </div>
          <p className="note" style={{ marginTop: 14 }}>
            Última actualización: {cuenta.passwordUpdatedLabel || '—'}
          </p>
        </div>
      </section>

      {isInternal && (
        <section className="set-section">
          <div className="set-section__head">
            <h3 className="set-section__title">Verificación</h3>
            <div className="set-section__right">
              <span className="note">
                Añade una capa extra de seguridad a tu cuenta.
              </span>
            </div>
          </div>
          <div className="set-section__body">
            <div className="pref pref--soon">
              <div className="prefrow__lbl">
                <span>
                  Verificación en dos pasos{' '}
                  <span className="soon-badge">Próx</span>
                </span>
                <span className="note">
                  Código por aplicación autenticadora (Google Authenticator,
                  Authy, etc.). Todavía no está implementada.
                </span>
              </div>
              <span className="switch">
                <span className="switch__dot" aria-hidden="true" />
              </span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// Preferencias — app-level preferences.
// Sections (in order): Apariencia · Funciones experimentales · Notificaciones.
function PreferenciasTab() {
  // ── Apariencia ──
  const [theme, setThemeState] = useState(resolveThemeMode);
  const handleTheme = (next) => {
    if (next === theme) return;
    const applied = toggleThemeMode(); // toggles between light↔dark
    setThemeState(applied);
  };

  const [accent, setAccent] = useAccent();
  const [density, setDensity] = useDensity();

  // ── Funciones experimentales ──
  // Internal-only: only internal (backoffice) users see or enable experimental
  // features, so the whole section is hidden for everyone else.
  const { isInternal } = useAuth() ?? {};
  const [exp, setExp] = useExpFeatures();

  // ── Notificaciones ──
  const [avisos, setAvisos] = useState(avisosPref.resolve);
  const [atajos, setAtajos] = useState(atajosPref.resolve);

  const handleAvisos = (next) => {
    setAvisos(avisosPref.set(next));
  };
  const handleAtajos = (next) => {
    setAtajos(atajosPref.set(next));
  };

  const ACCENTS = [
    { value: 'blue', label: 'Azul', color: 'var(--blue)' },
    { value: 'green', label: 'Verde', color: 'oklch(0.52 0.11 158)' },
    { value: 'neutral', label: 'Neutral', color: 'var(--ink)' },
  ];

  const prefRow = (label, hint, control) => (
    <div className="prefrow">
      <div className="prefrow__lbl">
        <span>{label}</span>
        <span className="note">{hint}</span>
      </div>
      {control}
    </div>
  );
  const toggle = (on, onToggle, label, hint) => (
    <div
      className="pref"
      role="switch"
      aria-checked={on}
      aria-label={label}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <div className="prefrow__lbl">
        <span>{label}</span>
        {hint && <span className="note">{hint}</span>}
      </div>
      <span className={`switch${on ? ' switch--on' : ''}`}>
        <span className="switch__dot" aria-hidden="true" />
      </span>
    </div>
  );

  return (
    <div className="settings">
      <section className="set-section">
        <div className="set-section__head">
          <h3 className="set-section__title">Apariencia</h3>
          <div className="set-section__right">
            <span className="note">
              Se aplica de inmediato y solo a tu cuenta.
            </span>
          </div>
        </div>
        <div className="set-section__body">
          {prefRow(
            'Tema',
            'Claro u oscuro para toda la interfaz.',
            <div className="seg">
              <button
                type="button"
                className={theme === 'light' ? 'is-on' : ''}
                onClick={() => handleTheme('light')}
              >
                Claro
              </button>
              <button
                type="button"
                className={theme === 'dark' ? 'is-on' : ''}
                onClick={() => handleTheme('dark')}
              >
                Oscuro
              </button>
            </div>,
          )}
          {prefRow(
            'Color de acento',
            'Color primario de botones y elementos activos.',
            <div className="swatchset">
              {ACCENTS.map(({ value, label, color }) => (
                <button
                  key={value}
                  type="button"
                  title={label}
                  aria-label={label}
                  aria-pressed={accent === value}
                  className={`swatch${accent === value ? ' is-on' : ''}`}
                  onClick={() => setAccent(value)}
                >
                  <span
                    className="swatch__dot"
                    style={{ background: color }}
                    aria-hidden="true"
                  />
                  {label}
                </button>
              ))}
            </div>,
          )}
          {prefRow(
            'Densidad de tablas',
            'Espacio vertical entre filas en las listas de datos.',
            <div className="seg">
              <button
                type="button"
                className={density === 'comoda' ? 'is-on' : ''}
                onClick={() => setDensity('comoda')}
              >
                Cómoda
              </button>
              <button
                type="button"
                className={density === 'compacta' ? 'is-on' : ''}
                onClick={() => setDensity('compacta')}
              >
                Compacta
              </button>
            </div>,
          )}
        </div>
      </section>

      <section className="set-section">
        <div className="set-section__head">
          <h3 className="set-section__title">Notificaciones</h3>
          <div className="set-section__right">
            <span className="note">Qué eventos generan alertas.</span>
          </div>
        </div>
        <div className="set-section__body">
          {toggle(
            avisos,
            () => handleAvisos(!avisos),
            'Avisos en la app',
            'Cambios de estado de tus diligencias y pagos.',
          )}
          {toggle(
            atajos,
            () => handleAtajos(!atajos),
            'Atajos de teclado',
            'Navegación con la secuencia «g + letra».',
          )}
          <div className="pref pref--soon">
            <div className="prefrow__lbl">
              <span>
                Resumen por correo <span className="soon-badge">Próx</span>
              </span>
              <span className="note">
                Digest diario de plazos y diligencias.
              </span>
            </div>
            <span className="switch">
              <span className="switch__dot" aria-hidden="true" />
            </span>
          </div>
        </div>
      </section>

      {isInternal && (
        <section className="set-section">
          <div className="set-section__head">
            <h3 className="set-section__title">Funciones experimentales</h3>
            <div className="set-section__right">
              <span className="note">
                Controles marcados «Próx» en todo el producto. Pueden cambiar o
                fallar.
              </span>
            </div>
          </div>
          <div className="set-section__body">
            {toggle(
              exp,
              () => setExp(!exp),
              'Funciones experimentales',
              'Activa secciones en desarrollo, como la selección en lote. El badge «Próx» se mantiene.',
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export function BancoTab({ cuentas, failed, onAgregar, onEditar, onEliminar }) {
  return (
    <div>
      <section className="cfg-section">
        <h3 className="cfg-section-title">Cuentas bancarias</h3>
        <p className="cfg-section-sub">
          Cuentas de la organización para liquidaciones y pagos a receptores.
        </p>
        {cuentas.length === 0 ? (
          <p
            className="cfg-section-sub"
            style={{ color: 'var(--ink-3)', marginBottom: 16 }}
          >
            {/* Una lectura caída y una organización sin cuenta rendían la misma
                frase, y la segunda invita a registrar una que quizá ya existe. */}
            {failed
              ? 'No se pudieron cargar las cuentas bancarias.'
              : 'Aún no has registrado una cuenta bancaria.'}
          </p>
        ) : (
          cuentas.map((c) => (
            <div key={c.id} className="cfg-card-tile">
              <div className="cfg-card-tile__info">
                <div className="cfg-card-brand">
                  {bankName(c.bankCode)}
                  {c.isDefault && (
                    <span className="cfg-principal-pill">Principal</span>
                  )}
                </div>
                <div className="cfg-card-pan">
                  ···· {String(c.accountNumber || '').slice(-4)}
                </div>
                <div className="cfg-card-meta">
                  {accountTypeLabel(c.accountType)} · {c.holderName}
                </div>
              </div>
              <div className="cfg-card-tile__actions">
                <button
                  type="button"
                  className="row-action"
                  onClick={() => onEditar && onEditar(c)}
                >
                  Editar
                </button>
                <button
                  type="button"
                  className="row-action danger"
                  onClick={() => onEliminar && onEliminar(c)}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))
        )}
        <div className="cfg-add-card-wrap">
          <Button variant="seal" size="md" onClick={onAgregar}>
            + Agregar cuenta bancaria
          </Button>
        </div>
      </section>
    </div>
  );
}

// Each "area" is its own /sistema/<area> page with its own section sub-nav.
// Both areas use the same horizontal top-tabs pattern.
const AREA_GROUPS = {
  cuenta: {
    label: 'Cuenta',
    items: [
      { key: 'perfil', label: 'Perfil' },
      { key: 'preferencias', label: 'Personalización' },
      { key: 'seguridad', label: 'Seguridad' },
    ],
  },
  organizacion: {
    label: 'Organización',
    items: [
      { key: 'datos', label: 'Datos del estudio' },
      { key: 'equipo', label: 'Equipo' },
    ],
  },
};

const SECTION_LABEL = Object.fromEntries(
  Object.values(AREA_GROUPS).flatMap((g) =>
    g.items.map((i) => [i.key, i.label]),
  ),
);

export default function ConfiguracionForm({
  area = 'cuenta',
  orgTitle,
  cuenta = {},
  generalData = {},
  perfil = {},
  perfilFailed = false,
  miembros = [],
  membersLoading = false,
  showMembers = true,
  // Role gate: only Administrador/owner users (permissions include 'equipo' /
  // 'estudio') can mutate member list and org profile. Defaults to true so
  // Storybook / tests that don't set it keep the actions enabled.
  canManageTeam = true,
  // Whether the acting user is the owner, and their user id, forwarded to the
  // member table's per-row gate.
  actingIsOwner = false,
  currentUserId,
  onEditar,
  onVerMiembro,
  onInvitar,
  onLeave,
  onReenviar,
  onSectionChange,
  activeTab: activeTabProp,
  onTabChange,
}) {
  const group = AREA_GROUPS[area] ?? AREA_GROUPS.cuenta;
  const heading =
    area === 'organizacion' ? (orgTitle ?? group.label) : group.label;
  // Personal accounts have no team, so the Equipo tab is hidden for them.
  const items = group.items.filter(
    (item) => item.key !== 'equipo' || showMembers,
  );
  // Pestaña controlable desde la URL (el route pasa activeTab/onTabChange);
  // sin control externo cae al estado interno (uso standalone/stories).
  const [internalTab, setInternalTab] = useState(items[0]?.key);
  const validTab = items.some((i) => i.key === activeTabProp)
    ? activeTabProp
    : null;
  const activeTab = validTab ?? internalTab;
  const selectTab = (key) =>
    onTabChange ? onTabChange(key) : setInternalTab(key);

  // Pagination for the equipo tab (must be top-level to avoid hooks-in-conditionals).
  const PAGE_SIZE = 8;
  const [membersPage, setMembersPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(miembros.length / PAGE_SIZE));
  // Clamp the page if the member list shrinks (e.g. after a refetch/removal)
  // so we never land on an out-of-range page that renders an empty table.
  useEffect(() => {
    if (membersPage > pageCount) setMembersPage(pageCount);
  }, [membersPage, pageCount]);
  const safePage = Math.min(membersPage, pageCount);
  const pagedMembers = miembros.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  // Report the active section so the route can mirror it in the breadcrumb.
  useEffect(() => {
    onSectionChange?.(SECTION_LABEL[activeTab] ?? '');
  }, [area, activeTab, onSectionChange]);

  // Sub derived from active cuenta tab so the page head reflects the current section.
  const CUENTA_SUB = {
    perfil: 'Tu nombre, correo y datos de identificación.',
    preferencias: 'Apariencia, accesibilidad y notificaciones.',
    seguridad: 'Contraseña y verificación en dos pasos.',
  };

  // cuenta area: render horizontal top tabs (organizacion uses the same pattern below).
  if (area === 'cuenta') {
    return (
      <div className="page reveal">
        <PageHead
          kicker="Sistema"
          title={heading}
          sub={CUENTA_SUB[activeTab] ?? 'Tu información personal y acceso.'}
        />

        <div className="tabs" role="tablist">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={activeTab === item.key}
              className={`tab${activeTab === item.key ? ' tab--on' : ''}`}
              onClick={() => selectTab(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="cfg-tab-panel">
          {activeTab === 'perfil' && (
            <PerfilTab cuenta={cuenta} onEditar={onEditar || (() => {})} />
          )}
          {activeTab === 'preferencias' && <PreferenciasTab />}
          {activeTab === 'seguridad' && (
            <SeguridadTab cuenta={cuenta} onEditar={onEditar || (() => {})} />
          )}
        </div>
      </div>
    );
  }

  // organizacion area — top-tabs pattern (same as cuenta).
  return (
    <div className="page reveal">
      <PageHead
        kicker="Sistema"
        title={heading}
        sub={showMembers ? 'Datos del estudio y equipo.' : 'Datos del estudio.'}
      />

      <div className="tabs" role="tablist">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={activeTab === item.key}
            className={`tab${activeTab === item.key ? ' tab--on' : ''}`}
            onClick={() => selectTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="cfg-tab-panel">
        {activeTab === 'datos' && (
          <PerfilOrgTab
            perfil={perfil}
            perfilFailed={perfilFailed}
            tipo={generalData?.tipo}
            onEditar={onEditar || (() => {})}
            canManageTeam={canManageTeam}
          />
        )}
        {activeTab === 'equipo' && (
          <>
            <OrgMiembrosTab
              miembros={pagedMembers}
              totalActivos={
                miembros.filter((m) => m.estado === 'active').length
              }
              onVerMiembro={onVerMiembro}
              onReenviar={onReenviar}
              onInvitar={onInvitar}
              loading={membersLoading}
              canManageTeam={canManageTeam}
              actingIsOwner={actingIsOwner}
              currentUserId={currentUserId}
              onLeave={onLeave}
            />
            <Pager
              page={safePage}
              pageCount={pageCount}
              total={miembros.length}
              pageSize={PAGE_SIZE}
              onPage={setMembersPage}
              unit="miembros"
            />
          </>
        )}
      </div>
    </div>
  );
}
